import { theme, Tooltip } from '@arnold/common';
import styled from '@emotion/styled/macro';
import { omit } from 'ramda';
import { FC } from 'react';
import { BarChartBody, ChartBar, ChartBarBox } from './StyledComponents';
import PulserChartTooltip from './PulserChartTooltip';
import { AnswerResult } from './pulserHelpers';

interface IProps {
  answerResults: AnswerResult[];
  notRelevantTotalCount: number;
  showEscapeOption?: boolean;
  percentage: number;
  width?: string;
  showPercentage?: boolean;
  percentageMargin?: 'small' | 'large';
}

/**
 * Reusable chart component for Pulser reports
 * Shows horizontal bar chart with answer distribution
 */
const PulserChart: FC<IProps> = ({
  answerResults,
  notRelevantTotalCount,
  showEscapeOption = true,
  percentage,
  width = '182px',
  showPercentage = true,
  percentageMargin = 'large',
}) => {
  const getBarColor = (answer: AnswerResult, index: number, array: AnswerResult[]) => {
    const previousLevels = array.slice(0, index).filter((a) => a.level === answer.level);
    const colors = theme.colors.chart[answer.level as keyof typeof theme.colors.chart];
    return colors[previousLevels.length % colors.length] ?? colors[0];
  };

  const visibleAnswers = answerResults.filter((answer) => answer.percentage > 0);

  return (
    <>
      <Tooltip
        delay={100}
        followCursor={false}
        html={
          <PulserChartTooltip
            answers={answerResults}
            notRelevantTotalCount={notRelevantTotalCount}
            showEscapeOption={showEscapeOption}
          />
        }
      >
        <BarChartBody width={width}>
          {visibleAnswers.map((answer, index, array) => {
            const color = getBarColor(answer, index, array);
            return (
              <ChartBarBox key={index} width={answer.percentage}>
                <ChartBar color={color} height="8px" />
              </ChartBarBox>
            );
          })}
        </BarChartBody>
      </Tooltip>
      {showPercentage && (
        <PercentageValue
          className="text-center"
          margin={percentageMargin}
          data-cy="report-pulser-question-item-percentage"
        >
          {percentage.toFixed(0)}%
        </PercentageValue>
      )}
    </>
  );
};

const PercentageValue = styled.div<{ margin: 'small' | 'large' }>(({ margin }) => ({
  ...omit(['family'], theme.typography.body.xLarge.bold),
  marginBottom: theme.spacing.b,
  marginRight: margin === 'small' ? 24 : 70,
  marginLeft: margin === 'small' ? 24 : 70,
  wordBreak: 'keep-all',
  width: '50px',
}));

export default PulserChart;
